export function date_convertToString(
  dateInput: Date,
  format = 'yyyy-MM-dd', // Don't change default because currently it using for http request
): string {
  if (!dateInput) return '';

  const date = date_init(dateInput)!;

  const map: Record<string, string> = {
    dd: `0${date.getDate()}`.slice(-2),
    MM: `0${date.getMonth() + 1}`.slice(-2),
    yyyy: `${date.getFullYear()}`,
    yy: `${date.getFullYear()}`.slice(-2),
    HH: `0${date.getHours()}`.slice(-2),
    mm: `0${date.getMinutes()}`.slice(-2),
    ss: `0${date.getSeconds()}`.slice(-2),
  };

  return format.replace(/yyyy|yy|MM|dd|HH|mm|ss/g, (match) => map[match]);
}

export function date_formatDateByLocale(
  date: Date | string | number | null | undefined,
  localeCode?: string,
  options?: Intl.DateTimeFormatOptions | undefined,
) {
  if (!date) return '';
  if (!localeCode) return date_init(date);

  return new Intl.DateTimeFormat(localeCode, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...options,
  }).format(date_init(date));
}

export const date_init = (
  dateInput: Date | string | number | null | undefined,
) =>
  !dateInput
    ? undefined
    : typeof dateInput === 'string' || typeof dateInput === 'number'
      ? new Date(dateInput)
      : dateInput;

/**
 * Format relative time (e.g., "5 minutes ago", "Yesterday")
 *
 * @param {string | undefined} dateString - ISO date string
 */
export function getRelativeTime(dateString: string | undefined): {
  key: string;
  params?: { n: number };
} {
  if (!dateString) return { key: '' };

  let date: Date;
  const dmyHmsRegex = /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/;
  const match = dateString.match(dmyHmsRegex);
  if (match) {
    const [, dd, MM, yyyy, HH, mm, ss] = match;
    date = new Date(
      parseInt(yyyy, 10),
      parseInt(MM, 10) - 1,
      parseInt(dd, 10),
      parseInt(HH, 10),
      parseInt(mm, 10),
      parseInt(ss, 10),
    );
  } else {
    date = new Date(dateString);
  }

  if (Number.isNaN(date.getTime())) return { key: '' };

  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return { key: 'TIME.JUST_NOW' };
  if (minutes < 60) return { key: 'TIME.MINUTES_AGO', params: { n: minutes } };
  if (hours < 24) return { key: 'TIME.HOURS_AGO', params: { n: hours } };
  if (days === 1) return { key: 'TIME.YESTERDAY' };
  return { key: 'TIME.DAYS_AGO', params: { n: days } };
}

/**
 * Get a date range relative to today.
 *
 * @param days Number of days before today (e.g., 7 for last week)
 * @param format Format string (default: 'dd/MM/yyyy')
 */
export function date_getRelativeRange(days: number, format = 'dd/MM/yyyy') {
  const today = new Date();
  const fromDate = new Date();
  fromDate.setDate(today.getDate() - days);

  return {
    from_date: date_convertToString(fromDate, format),
    to_date: date_convertToString(today, format),
  };
}

/**
 * So sánh hai chuỗi ngày (dd/MM/yyyy)
 * @returns {boolean} true nếu startDate > endDate
 */
export function compareDate(startDate: string, endDate: string): boolean {
  if (!startDate || !endDate) return false;

  const parse = (s: string) => {
    const [d, m, y] = s.split('/').map(Number);
    return new Date(y, m - 1, d).getTime();
  };

  return parse(startDate) > parse(endDate);
}

export function date_addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);

  return result;
}

export function date_remainingDays(fromDate: Date, targetDate: Date): number {
  const from = date_init(fromDate);
  const target = date_init(targetDate);
  if (!target) return 0;

  const diffTime =
    target.getTime() - (from ? from.getTime() : new Date().getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}
